import React, { useState, useEffect, useMemo } from 'react';
import ResearchLayout from '../../components/ResearchLayout';
import Pagination from '../../components/Pagination';
import { protocolService } from '../../services/protocolService';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import Modal from '../../components/Modal';
import MeasurementDisplay from '../../components/MeasurementDisplay';

const getColorName = (prediction) => {
  if (!prediction) return 'N/A';
  const colorMap = {
    "Excellent": "Excellent",
    "Good": "Good",
    "Fair": "Fair",
    "High Risk": "High Risk",
    "Critical": "Critical"
  };
  // If prediction is a color code, map it to a name if possible
  if (typeof prediction === "string" && prediction.startsWith("#")) {
    const hexMap = {
      "#83D65C": "Excellent",
      "#5BC0DE": "Good",
      "#ffe366": "Good",
      "#ff944d": "Fair",
      "#ff6666": "High Risk",
      "#d9534f": "Critical"
    };
    return hexMap[prediction] || prediction;
  }
  // Otherwise, return the prediction as-is or mapped name
  return colorMap[prediction] || prediction;
};

const MedicalAdminAssignCases = () => {
  const { user } = useAuth();
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [assignMultiple, setAssignMultiple] = useState(false);
  const [selectedDoctors, setSelectedDoctors] = useState([]); // for multi-assign
  const [selectedProtocols, setSelectedProtocols] = useState([]); // for multi-assign
  const [selectedCases, setSelectedCases] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [protocolDetail, setProtocolDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [cases, setCases] = useState([]);
  const [assignments, setAssignments] = useState([]);
  // reliable set of case IDs assigned to the currently selected doctor
  const [assignedCases, setAssignedCases] = useState([]);
  const [batchInfo, setBatchInfo] = useState([]); // Batch information for cases
  const [showAssignedCasesModal, setShowAssignedCasesModal] = useState(false);
  const [assignedCasesMessage, setAssignedCasesMessage] = useState('');
  const [showReassignAlert, setShowReassignAlert] = useState(false);
  const [reassignAlertMessage, setReassignAlertMessage] = useState('');
  const [showSelectDoctorProtocolAlert, setShowSelectDoctorProtocolAlert] = useState(false);
  const [selectDoctorProtocolMessage, setSelectDoctorProtocolMessage] = useState('');

  // Pagination and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const casesPerPage = 50;

  // Server-side filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [bmiMin, setBmiMin] = useState('');
  const [bmiMax, setBmiMax] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [genderFilter, setGenderFilter] = useState([]); // array of strings
  const [overallGradeMin, setOverallGradeMin] = useState('');
  const [overallGradeMax, setOverallGradeMax] = useState('');
  const [completeProfileOnly, setCompleteProfileOnly] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  const colorMap = {
    "#83D65C": "Excellent",
    "#5BC0DE": "Good",
    "#ffe366": "Good",
    "#ff944d": "Fair",
    "#ff6666": "High Risk",
    "#d9534f": "Critical"
  };

  useEffect(() => {
    loadProtocols();
    loadDoctors();
    loadCases();
    loadBatchInfo();
    loadAssignments();
    loadAssignedCases();
  }, []);

  // Ensure any stray timer element (inserted elsewhere) is removed and
  // make sure protocol description updates whenever selection changes.
  useEffect(() => {
    // look for elements that might be rendering a timer and remove them
    try {
      const timerById = document.getElementById('protocol-timer');
      if (timerById && timerById.parentNode) timerById.parentNode.removeChild(timerById);
      const timerByClass = document.querySelector('.protocol-timer');
      if (timerByClass && timerByClass.parentNode) timerByClass.parentNode.removeChild(timerByClass);
    } catch (e) {
      // no-op
    }
  }, [selectedProtocol, selectedCases]);

  // When a protocol is selected, fetch its full details (including questions)
  // and keep a small cached protocolDetail to compute accurate question counts.
  useEffect(() => {
    let cancelled = false;
    const fetchDetail = async () => {
      if (!selectedProtocol) {
        setProtocolDetail(null);
        return;
      }
      try {
        const resp = await protocolService.getProtocol(selectedProtocol, true);
        // normalize different possible response shapes
        const data = resp && (resp.data || resp);
        const p = data && (data.protocol || data || null);
        if (!cancelled) setProtocolDetail(p || null);
      } catch (err) {
        if (!cancelled) setProtocolDetail(null);
      }
    };
    fetchDetail();
    return () => { cancelled = true; };
  }, [selectedProtocol]);

  const loadDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      const activeDoctors = (response.data.doctors || []).filter(
        doctor => doctor.is_active !== false && !doctor.email.startsWith('deleted_')
      );
      setAvailableDoctors(activeDoctors);
    } catch (error) {
      setAvailableDoctors([]);
      console.error('Failed to fetch doctors:', error);
    }
  };

  const loadCases = async () => {
    try {
      const response = await api.get('/cases/assigned-to-med');
      setCases(response.data.cases || []);
    } catch (err) {
      setCases([]);
    }
  };

  const loadBatchInfo = async () => {
    try {
      const response = await api.get('/batches?with_case_counts=true');
      setBatchInfo(response.data.batches || []);
    } catch (err) {
      console.error('Error loading batch info:', err);
      setBatchInfo([]);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await api.get('/assignments/details?assigned_by=' + user.id);
      if (response.data && response.data.assignments) {
        setAssignments(response.data.assignments);
      } else {
        setAssignments([]);
      }
    } catch (err) {
      setAssignments([]);
      console.error("Error loading assignment details:", err);
    }
  };

  const loadAssignedCases = async () => {
    try {
      const response = await api.get('/assignments');
      const raw = (response.data && response.data.assignments) || [];

      // normalize assignments (ensure numeric case_id and doctor_id fields)
      const normalized = raw.map(a => {
        const caseIdCandidates = [
          a.case_id, a.caseId, a.assessment_id, a.assessmentId,
          a._case_id,
          a.case && (a.case.id ?? a.case.case_id ?? a.caseId),
          a.assessment && (a.assessment.id ?? a.assessment.assessment_id ?? a.assessmentId)
        ];
        const foundCase = caseIdCandidates.find(v => v !== undefined && v !== null);
        const case_id = (foundCase === undefined || foundCase === null) ? undefined : Number(foundCase);

        // doctor id normalization
        const docCandidates = [
          a.doctor_id, a.doctorId, (a.doctor && (a.doctor.id ?? a.doctor.user_id)), (a.user && a.user.id)
        ];
        const foundDoc = docCandidates.find(v => v !== undefined && v !== null);
        const doctor_id = (foundDoc === undefined || foundDoc === null) ? undefined : Number(foundDoc);

        return {
          ...a,
          case_id: Number.isFinite(case_id) ? case_id : undefined,
          doctor_id: Number.isFinite(doctor_id) ? doctor_id : undefined
        };
      });

      setAssignedCases(normalized);
    } catch (error) {
      console.error("Error loading assigned cases:", error);
      setAssignedCases([]);
    }
  };

  const loadProtocols = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await protocolService.getProtocols();
      const protocolsArr =
        (response.data && response.data.protocols) ||
        response.protocols ||
        [];
      setProtocols(protocolsArr);
    } catch (err) {
      console.error('Error loading protocols:', err);
      setError('Failed to load protocols. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const availableProtocols = protocols;

  // Memoized selected protocol object and derived duration (2 minutes per question)
  const selectedProtocolObj = React.useMemo(() => {
    if (!selectedProtocol) return null;
  return availableProtocols.find(p => String(p.id) === String(selectedProtocol)) || null;
  }, [availableProtocols, selectedProtocol]);

  const selectedProtocolQuestionCount = React.useMemo(() => {
  // Prefer freshly fetched protocolDetail (includes full questions array) when available
  const p = protocolDetail || selectedProtocolObj;
  if (!p) return 0;
  return p.questions_count || p.questionsCount || (Array.isArray(p.questions) ? p.questions.length : 0) || 0;
  }, [selectedProtocolObj, protocolDetail]);

  const selectedProtocolEstimatedMinutes = React.useMemo(() => {
    const q = Number(selectedProtocolQuestionCount) || 0;
    // 2 minutes per question
    return Math.ceil((q * 2));
  }, [selectedProtocolQuestionCount]);

  // Total estimated minutes across selected cases (per-case minutes * number of selected cases)
  const selectedProtocolTotalEstimatedMinutes = React.useMemo(() => {
    const perCase = Number(selectedProtocolEstimatedMinutes) || 0;
    const count = Number(selectedCases.length) || 0;
    return perCase * count;
  }, [selectedProtocolEstimatedMinutes, selectedCases.length]);

  const getAssignmentCaseId = (a) => {
    return Number.isFinite(Number(a.case_id)) ? Number(a.case_id) :
      (Number.isFinite(Number(a.caseId)) ? Number(a.caseId) : undefined);
  };
  const getAssignmentDoctorId = (a) => {
    return Number.isFinite(Number(a.doctor_id)) ? Number(a.doctor_id) :
      (Number.isFinite(Number(a.doctorId)) ? Number(a.doctorId) : undefined);
  };

  // Get batch info for a case
  const getBatchInfoForCase = (caseId) => {
    // Check which batches contain this case
    return batchInfo.filter(batch => {
      // A case belongs to a batch if the batch contains it in its case_count
      // This is a simplified approach - in a real app, you'd have a proper case-batch relationship
      return batch.case_count > 0; // For now, show all batches with cases
    });
  };

  // Check if case is in batch with specific status
  const isCaseInBatchStatus = (caseId, status) => {
    if (status === 'all' || status === 'no_batch') return true;
    
    const caseBatches = getBatchInfoForCase(caseId);
    if (status === 'no_batch') {
      return caseBatches.length === 0;
    }
    
    // Check if case is in any batch with the specified status
    return caseBatches.some(batch => {
      if (status === 'active') return batch.is_active;
      if (status === 'scheduled') return !batch.is_active && !batch.is_expired;
      if (status === 'expired') return batch.is_expired;
      return false;
    });
  };

  // check assignment optionally for a specific doctor
  const isCaseAssignedToDoctor = (caseId, doctorId = null) => {
    if (!assignedCases || assignedCases.length === 0) return false;
    const cid = Number(caseId);
    if (Number.isNaN(cid)) return false;
    if (doctorId !== null && doctorId !== undefined && doctorId !== '') {
      const did = Number(doctorId);
      if (Number.isNaN(did)) return false;
      return assignedCases.some(a => getAssignmentCaseId(a) === cid && getAssignmentDoctorId(a) === did);
    }
    return assignedCases.some(a => getAssignmentCaseId(a) === cid);
  };

  // set default assignment filter to 'assigned' when a doctor is selected
  useEffect(() => {
    if (selectedDoctor) {
      setAssignmentFilter('assigned');
      setCurrentPage(1);
    }
  }, [selectedDoctor]);

  // memoized set of case ids assigned to selected doctor
  const assignedCaseIdsForSelectedDoctor = useMemo(() => {
    const s = new Set();
    if (!selectedDoctor) return s;
    const did = Number(selectedDoctor);
    if (Number.isNaN(did)) return s;
    (assignedCases || []).forEach(a => {
      const cid = getAssignmentCaseId(a);
      const aid = getAssignmentDoctorId(a);
      if (cid !== undefined && aid === did) s.add(Number(cid));
    });
    return s;
  }, [assignedCases, selectedDoctor]);

  // Filtering logic
  const filteredCases = cases.filter(case_ => {
    const matchesSearch =
      String(case_.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (case_.physicalHealthPrediction || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (case_.mentalHealthPrediction || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    let assignmentMatch = false;
    if (!selectedDoctor) {
      // previous global behavior when no doctor selected
      if (assignmentFilter === 'all') assignmentMatch = true;
      else if (assignmentFilter === 'assigned') assignmentMatch = isCaseAssignedToDoctor(case_.id);
      else if (assignmentFilter === 'unassigned') assignmentMatch = !isCaseAssignedToDoctor(case_.id);
    } else {
      // doctor-specific filtering
      if (assignmentFilter === 'all') assignmentMatch = true;
      else if (assignmentFilter === 'assigned') assignmentMatch = assignedCaseIdsForSelectedDoctor.has(Number(case_.id));
      else if (assignmentFilter === 'unassigned') assignmentMatch = !assignedCaseIdsForSelectedDoctor.has(Number(case_.id));
    }
    
    // Apply batch status filter
    const batchMatch = isCaseInBatchStatus(case_.id, batchStatusFilter);
    
    return matchesSearch && assignmentMatch && batchMatch;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredCases.length / casesPerPage);
  const paginatedCases = filteredCases.slice((currentPage - 1) * casesPerPage, currentPage * casesPerPage);

  // IDs of cases visible on the current page (used for Select Page). Allow selecting assigned cases too.
  const visiblePageIds = paginatedCases.map(c => c.id);
  const allVisibleSelected = visiblePageIds.length > 0 && visiblePageIds.every(id => selectedCases.includes(id));

  // Reset to first page when filters/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, assignmentFilter, batchStatusFilter, cases]);

  const handleCaseToggle = (caseId) => {
    setSelectedCases(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };

  const handleSelectAll = () => {
    // Toggle selection for all cases visible on the current page (include assigned)
    const pageIds = visiblePageIds;
    if (pageIds.length === 0) return;
    if (allVisibleSelected) {
      // deselect visible page cases
      setSelectedCases(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      // add visible page cases to selection (preserve other selections)
      setSelectedCases(prev => [
        ...prev,
        ...pageIds.filter(id => !prev.includes(id))
      ]);
    }
  };

  const handleAssignCases = async () => {
    // Validation
    if (!selectedProtocol) {
      setSelectDoctorProtocolMessage("Please select a protocol.");
      setShowSelectDoctorProtocolAlert(true);
      return;
    }

    const doctorsToAssign = assignMultiple ? selectedDoctors : (selectedDoctor ? [selectedDoctor] : []);
    if (doctorsToAssign.length === 0) {
      setSelectDoctorProtocolMessage("Please select at least one doctor.");
      setShowSelectDoctorProtocolAlert(true);
      return;
    }

    if (selectedCases.length === 0) {
      setReassignAlertMessage(`Please select cases to be assigned`);
      setShowReassignAlert(true);
      return;
    }

    try {
      setIsSubmitting(true);
      let totalAssignments = 0;
      let successfulDoctors = [];
      let failedDoctors = [];

      // Process each doctor separately
      for (const doctorId of doctorsToAssign) {
        try {
          // Check for exact duplicate assignments for this doctor
          const alreadyExact = selectedCases.filter(caseId =>
            assignments.some(a =>
              Number(a.case_id) === Number(caseId) &&
              Number(a.doctor_id) === Number(doctorId) &&
              Number(a.protocol_id) === Number(selectedProtocol)
            )
          );

          // Only assign cases that aren't already assigned to this doctor with this protocol
          const casesToAssign = selectedCases.filter(caseId => !alreadyExact.includes(caseId));
          
          if (casesToAssign.length > 0) {
            const payload = {
              doctor_id: Number(doctorId),
              protocol_id: Number(selectedProtocol),
              case_ids: casesToAssign
            };
            
            const response = await api.post('/cases/batch-assign', payload);
            if (response.status === 201 || response.status === 200) {
              const doctorName = availableDoctors.find(d => Number(d.id) === Number(doctorId))?.name || `Doctor ${doctorId}`;
              successfulDoctors.push(doctorName);
              totalAssignments += response.data.assignments_created || casesToAssign.length;
            }
          } else {
            const doctorName = availableDoctors.find(d => Number(d.id) === Number(doctorId))?.name || `Doctor ${doctorId}`;
            failedDoctors.push(`${doctorName} (all cases already assigned)`);
          }
        } catch (error) {
          const doctorName = availableDoctors.find(d => Number(d.id) === Number(doctorId))?.name || `Doctor ${doctorId}`;
          failedDoctors.push(`${doctorName} (${error.response?.data?.message || 'assignment failed'})`);
        }
      }

      // Prepare success message
      let message = '';
      if (successfulDoctors.length > 0) {
        message += `Successfully assigned ${totalAssignments} case(s) to ${successfulDoctors.length} doctor(s): ${successfulDoctors.join(', ')}.`;
      }
      if (failedDoctors.length > 0) {
        message += ` ${failedDoctors.length > 0 ? '\n\n' : ''}Issues with: ${failedDoctors.join(', ')}.`;
      }

      if (successfulDoctors.length > 0) {
        setAssignedCasesMessage(message);
        setShowAssignedCasesModal(true);
        setSelectedCases([]);
        setSelectedDoctor("");
        setSelectedDoctors([]);
        setSelectedProtocol("");
        setAssignMultiple(false);
        await loadAssignments();
        await loadCases();
        await loadAssignedCases();
      } else {
        setReassignAlertMessage(message || 'No assignments could be created.');
        setShowReassignAlert(true);
      }
    } catch (error) {
      console.error("Error assigning cases:", error);
      setError(`Failed to assign cases: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHealthColorClass = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  const getScoreBadgeClass = (score) => {
    if (score >= 80) return 'bg-success';
    if (score >= 60) return 'bg-warning';
    return 'bg-danger';
  };

  useEffect(() => {
    console.log("Button would be disabled:", {
      isSubmitting,
      selectedProtocolEmpty: !selectedProtocol,
      selectedDoctorEmpty: !selectedDoctor,
      noCasesSelected: selectedCases.length === 0
    });
  }, [isSubmitting, selectedProtocol, selectedDoctor, selectedCases]);

  if (loading) {
    return (
      <ResearchLayout pageTitle="Assign Cases to Doctors">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading protocols...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Assign Cases to Doctors">
      <div className="container-fluid">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={loadProtocols}
            >
              Try Again
            </button>
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Success Alert */}
        {showSuccess && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Success!</strong> Cases assigned to doctor successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        )}

        {/* Assignment Form */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-tasks me-2"></i>
                  Create New Assignment
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label htmlFor="protocol" className="form-label fw-bold">
                      <i className="fas fa-clipboard-list me-2 text-primary"></i>
                      Select Protocol *
                    </label>
                    <select
                      className="form-select form-select-lg"
                      id="protocol"
                      value={selectedProtocol}
                      onChange={(e) => setSelectedProtocol(e.target.value)}
                    >
                      <option value="">Choose a protocol...</option>
                      {availableProtocols.map(protocol => (
                        <option key={protocol.id} value={protocol.id}>
                          {protocol.name}
                        </option>
                      ))}
                    </select>
                    {selectedProtocol && (
                        <div className="mt-2 p-2 bg-light rounded small">
                          {/* <div>{selectedProtocolObj?.description}</div> */}
                          <div className="text-muted small mt-1">
                            Questions: <strong>{selectedProtocolQuestionCount}</strong>
                            {' — '}Estimated time per case: <strong>{selectedProtocolEstimatedMinutes} min</strong>
                            {selectedCases.length > 0 && (
                              <>
                                {' — '}Total for <strong>{selectedCases.length}</strong> case(s): <strong>{selectedProtocolTotalEstimatedMinutes} min</strong>
                              </>
                            )}
                          </div>
                        </div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label fw-bold mb-0">
                        <i className="fas fa-user-md me-2 text-primary"></i>
                        Select Doctor(s) *
                      </label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="assignMultiple"
                          checked={assignMultiple}
                          onChange={(e) => {
                            setAssignMultiple(e.target.checked);
                            if (!e.target.checked) {
                              setSelectedDoctors([]);
                            } else {
                              setSelectedDoctor('');
                            }
                          }}
                        />
                        <label className="form-check-label small" htmlFor="assignMultiple">
                          Multiple Doctors
                        </label>
                      </div>
                    </div>
                    
                    {!assignMultiple ? (
                      <>
                        <select
                          className="form-select form-select-lg"
                          id="doctor"
                          value={selectedDoctor}
                          onChange={(e) => setSelectedDoctor(e.target.value)}
                        >
                          <option value="">Choose a doctor...</option>
                          {availableDoctors.map(doctor => (
                            <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                          ))}
                        </select>
                        {selectedDoctor && (
                          <div className="mt-2 p-2 bg-light rounded small">
                            <i className="fas fa-envelope me-2"></i>
                            {availableDoctors.find(d => d.id === parseInt(selectedDoctor))?.email}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="border rounded p-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {availableDoctors.map(doctor => (
                            <div key={doctor.id} className="form-check mb-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`doctor-${doctor.id}`}
                                checked={selectedDoctors.includes(doctor.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDoctors(prev => [...prev, doctor.id]);
                                  } else {
                                    setSelectedDoctors(prev => prev.filter(id => id !== doctor.id));
                                  }
                                }}
                              />
                              <label className="form-check-label small" htmlFor={`doctor-${doctor.id}`}>
                                {doctor.name}
                              </label>
                            </div>
                          ))}
                        </div>
                        {selectedDoctors.length > 0 && (
                          <div className="mt-2 p-2 bg-light rounded small">
                            <i className="fas fa-users me-2"></i>
                            {selectedDoctors.length} doctor(s) selected
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="col-md-4 mb-3 d-flex align-items-end">
                    <div className="d-grid w-100">
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleSelectAll}
                      >
                        <i className="fas fa-check-square me-2"></i>
                        {allVisibleSelected ? 'Deselect Page' : 'Select Page'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    {selectedCases.length} case(s) selected
                    {assignMultiple && selectedDoctors.length > 0 && (
                      <span className="ms-2">
                        • {selectedDoctors.length} doctor(s) selected
                      </span>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-lg px-4"
                      onClick={handleAssignCases}
                      disabled={
                        isSubmitting ||
                        selectedCases.length === 0 ||
                        !selectedProtocol ||
                        (!assignMultiple && !selectedDoctor) ||
                        (assignMultiple && selectedDoctors.length === 0)
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Assigning...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane me-2"></i>
                          Create Assignment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="row mb-4">
          <div className="col-md-4 mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Search cases by ID or health prediction..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3 mb-2">
            <select
              className="form-select"
              value={assignmentFilter}
              onChange={e => setAssignmentFilter(e.target.value)}
            >
              <option value="all">All Cases</option>
              <option value="assigned">Assigned Cases</option>
              <option value="unassigned">Unassigned Cases</option>
            </select>
          </div>
          <div className="col-md-3 mb-2">
            <select
              className="form-select"
              value={batchStatusFilter}
              onChange={e => setBatchStatusFilter(e.target.value)}
            >
              <option value="all">All Batches</option>
              <option value="active">Cases in Active Batches</option>
              <option value="scheduled">Cases in Scheduled Batches</option>
              <option value="expired">Cases in Expired Batches</option>
              <option value="no_batch">Cases Not in Any Batch</option>
            </select>
          </div>
          <div className="col-md-2 mb-2 text-end">
            <button
              className="btn btn-outline-primary"
              onClick={handleSelectAll}
            >
              <i className="fas fa-check-square me-2"></i>
              {allVisibleSelected ? 'Deselect Page' : 'Select Page'}
            </button>
          </div>
        </div>
        
        {/* Filter Status Indicator */}
        {batchStatusFilter !== 'all' && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="alert alert-info">
                <i className="fas fa-filter me-2"></i>
                <strong>Batch Filter Active:</strong>
                Showing cases from {batchStatusFilter === 'no_batch' ? 'cases not in any batch' : batchStatusFilter + ' batches'}
              </div>
            </div>
          </div>
        )}

        {/* Cases Selection Grid with Pagination */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-users me-2 text-primary"></i>
                  Select Cases for Assignment ({filteredCases.length} available)
                </h6>
              </div>
              <div className="card-body">
                <div className="row">
                  {paginatedCases.map((case_) => (
                    <div key={case_.id} className="col-lg-4 col-md-6 mb-3">
                      <div className={`card h-100 ${selectedCases.includes(case_.id) ? 'border-primary shadow' : 'border'}`}>
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`case-${case_.id}`}
                              checked={selectedCases.includes(case_.id)}
                              onChange={() => handleCaseToggle(case_.id)}
                            />
                            <label className="form-check-label fw-bold" htmlFor={`case-${case_.id}`}>
                              {case_.id} {/* Display case ID */}
                            </label>
                          </div>
                          <div>
                            <span className={`badge ${getScoreBadgeClass(case_.score)}`}>
                              {case_.score}
                            </span>
                            {selectedDoctor ? (
                              (assignedCaseIdsForSelectedDoctor.has(Number(case_.id)) ? (
                                <span className="badge bg-danger ms-2">Assigned</span>
                              ) : (
                                <span className="badge bg-secondary ms-2">Not assigned</span>
                              ))
                            ) : (
                              isCaseAssignedToDoctor(case_.id) && (
                                <span className="badge bg-success ms-2">Assigned to Doctor</span>
                              )
                            )}
                          </div>
                        </div>
                        <div className="card-body">
                          {/* Demographics */}
                          <div className="row mb-3">
                            <div className="col-6">
                              <div className="small text-muted">Age</div>
                              <div className="fw-bold small">{case_.age || 'N/A'}</div>
                            </div>
                            <div className="col-6">
                              <div className="small text-muted">Gender</div>
                              <div className="fw-bold small">{case_.gender || 'N/A'}</div>
                            </div>
                          </div>

                          {/* Health Predictions */}
                          <div className="row mb-2">
                            <div className="col-6">
                              <div className="small text-muted">Physical</div>
                              <div className={`fw-bold small ${getHealthColorClass(case_.physicalHealthPrediction)}`}>
                                {getColorName(case_.physicalHealthPrediction)}
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="small text-muted">Mental</div>
                              <div className={`fw-bold small ${getHealthColorClass(case_.mentalHealthPrediction)}`}>
                                {getColorName(case_.mentalHealthPrediction)}
                              </div>
                            </div>
                          </div>

                          {/* Basic Info */}
                          <div className="row mb-2">
                            <div className="col-4">
                              <div className="small text-muted">Weight</div>
                              <div className="fw-bold small">
                                <MeasurementDisplay
                                  measurementType="weight"
                                  value={case_.weight}
                                  originalUnit={case_.weightUnit}
                                  showLabel={false}
                                />
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="small text-muted">BMI</div>
                              <div className="fw-bold small">
                                {case_.weight && case_.height ?
                                  (case_.weight / Math.pow(case_.height / 100, 2)).toFixed(1) : 'N/A'}
                              </div>
                            </div>
                            <div className="col-4">
                              <div className="small text-muted">BP</div>
                              <div className="fw-bold small">
                                {case_.systolicBloodPressure}/{case_.diastolicBloodPressure}
                              </div>
                            </div>
                          </div>

                          {/* Risk Indicators */}
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {case_.isSmoker && (
                              <span className="badge bg-danger">Smoker</span>
                            )}
                            {case_.isDrinker && (
                              <span className="badge bg-warning">Drinks</span>
                            )}
                            {case_.diagnosedChronicDisease !== 'None' && (
                              <span className="badge bg-info">Chronic</span>
                            )}
                          </div>

                          {/* AI Preview */}
                          {/* <div className="small text-muted">
                            <strong>AI:</strong> {case_.aiResponse?.substring(0, 50)}...
                          </div> */}
                        </div>
                        <div className="card-footer bg-light small">
                          <div className="d-flex justify-content-between">
                            <span>Completion: {case_.assessmentCompletionPercentage}%</span>
                            <span className="text-muted">
                              {new Date(case_.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination Controls */}
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    showInfo={true}
                    totalItems={filteredCases.length}
                    itemsPerPage={casesPerPage}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Assignments */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <i className="fas fa-history me-2 text-primary"></i>
                  Recent Assignments ({assignments.length})
                </h6>
              </div>
              <div className="card-body" style={{ overflow: 'visible' }}>
                {assignments.length > 0 ? (
                  <>
                    {/* Desktop Table */}
                    <div className="table-responsive d-none d-md-block">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Doctor</th>
                            <th>Protocols</th>
                            <th>Cases</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(
                            assignments.reduce((acc, assignment) => {
                              const doctor = availableDoctors.find(
                                d => d.id === assignment.doctor_id
                              );
                              const doctorName = doctor?.name || 'Unknown Doctor';
                              if (!acc[doctorName]) {
                                acc[doctorName] = {
                                  doctor: doctor,
                                  protocols: [],
                                  cases: []
                                };
                              }
                              if (
                                assignment.protocol_id &&
                                !acc[doctorName].protocols.find(
                                  p => p.id === assignment.protocol_id
                                )
                              ) {
                                const protocol = availableProtocols.find(
                                  p => p.id === assignment.protocol_id
                                );
                                if (protocol) {
                                  acc[doctorName].protocols.push(protocol);
                                }
                              }
                              // Add caseId to the cases array
                              if (assignment.case_id) {
                                acc[doctorName].cases.push(assignment.case_id);
                              }
                              return acc;
                            }, {})
                          ).sort(([, a], [, b]) => {
                            // Handle cases where doctor might be undefined (deleted doctors)
                            const nameA = a.doctor?.name || 'Unknown';
                            const nameB = b.doctor?.name || 'Unknown';
                            return nameA.localeCompare(nameB);
                          })
                          .slice(0, 5) // Limit to 5 doctors
                          .map(([doctorName, { doctor, protocols, cases }]) => (
                            <tr key={doctor?.id || doctorName}>
                              <td>
                                <i className="fas fa-user-md text-success me-2"></i>
                                {doctor ? doctorName : <span className="text-danger">{doctorName} (Deleted)</span>}
                              </td>
                              <td style={{ position: 'relative' }}>
                                <div className="dropdown">
                                  <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    View Protocols ({protocols.length})
                                  </button>
                                  <ul className="dropdown-menu" style={{ zIndex: 1000 }}>
                                    {protocols.length > 0 ? (
                                      protocols.map((protocol, index) => (
                                        <li key={`${protocol.id}-${index}`}>
                                          <a className="dropdown-item" onClick={e => e.preventDefault()}>
                                            {protocol.name}
                                          </a>
                                        </li>
                                      ))
                                    ) : (
                                      <li><span className="dropdown-item">No Protocols Assigned</span></li>
                                    )}
                                  </ul>
                                </div>
                              </td>
                              <td style={{ position: 'relative' }}>
                                <div className="dropdown">
                                  <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                    View Cases ({cases.length})
                                  </button>
                                  <ul className="dropdown-menu" style={{ zIndex: 1000 }}>
                                    {cases.length > 0 ? (
                                      cases.map((caseId, index) => (
                                        <li key={`${caseId}-${index}`}>
                                          <a className="dropdown-item" onClick={e => e.preventDefault()}>
                                            {caseId}
                                          </a>
                                        </li>
                                      ))
                                    ) : (
                                      <li><span className="dropdown-item">No Cases Assigned</span></li>
                                    )}
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile Cards */}
                    <div className="d-block d-md-none">
                      {Object.entries(
                        assignments.reduce((acc, assignment) => {
                          const doctor = availableDoctors.find(
                            d => d.id === assignment.doctor_id
                          );
                          const doctorName = doctor?.name || 'Unknown Doctor';
                          if (!acc[doctorName]) {
                            acc[doctorName] = {
                              doctor: doctor,
                              protocols: [],
                              cases: []
                            };
                          }
                          if (
                            assignment.protocol_id &&
                            !acc[doctorName].protocols.find(
                              p => p.id === assignment.protocol_id
                            )
                          ) {
                            const protocol = availableProtocols.find(
                              p => p.id === assignment.protocol_id
                            );
                            if (protocol) {
                              acc[doctorName].protocols.push(protocol);
                            }
                          }
                          // Add caseId to the cases array
                          if (assignment.case_id) {
                            acc[doctorName].cases.push(assignment.case_id);
                          }
                          return acc;
                        }, {})
                      ).sort(([, a], [, b]) => {
                        const nameA = a.doctor?.name || 'Unknown';
                        const nameB = b.doctor?.name || 'Unknown';
                        return nameA.localeCompare(nameB);
                      })
                      .slice(0, 5)
                      .map(([doctorName, { doctor, protocols, cases }]) => (
                        <div key={doctor?.id || doctorName} className="card mb-3 border border-primary">
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-user-md text-success me-2"></i>
                              <div>
                                <div className="fw-bold">{doctor ? doctorName : <span className="text-danger">{doctorName} (Deleted)</span>}</div>
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Protocols:</strong>
                              <ul className="list-unstyled mb-1">
                                {protocols.length > 0 ? (
                                  protocols.map((protocol, idx) => (
                                    <li key={protocol.id || idx}>
                                      <span className="badge bg-info text-dark me-1">{protocol.name}</span>
                                    </li>
                                  ))
                                ) : (
                                  <li><span className="text-muted">No Protocols Assigned</span></li>
                                )}
                              </ul>
                            </div>
                            <div className="mb-2">
                              <strong>Cases:</strong>
                              <ul className="list-unstyled mb-1">
                                {cases.length > 0 ? (
                                  cases.map((caseId, idx) => (
                                    <li key={caseId || idx}>
                                      <span className="badge bg-secondary me-1">{caseId}</span>
                                    </li>
                                  ))
                                ) : (
                                  <li><span className="text-muted">No Cases Assigned</span></li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-tasks fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Assignments Created Yet</h6>
                    <p className="text-muted">
                      Create your first assignment using the form above.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Cases Success Modal */}
        <Modal
          isOpen={showAssignedCasesModal}
          onClose={() => setShowAssignedCasesModal(false)}
          title="Cases Assigned Successfully"
          confirmText="Close"
          onConfirm={() => setShowAssignedCasesModal(false)}
        >
          <p>{assignedCasesMessage}</p>
        </Modal>

        {/* Reassign Alert Modal */}
        <Modal
          isOpen={showReassignAlert}
          onClose={() => setShowReassignAlert(false)}
          title="Cannot Reassign"
          confirmText="Close"
          onConfirm={() => setShowReassignAlert(false)}
        >
          <p>{reassignAlertMessage}</p>
        </Modal>

        {/* Select Doctor/Protocol Alert Modal */}
        <Modal
          isOpen={showSelectDoctorProtocolAlert}
          onClose={() => setShowSelectDoctorProtocolAlert(false)}
          title="Selection Required"
          confirmText="Close"
          onConfirm={() => setShowSelectDoctorProtocolAlert(false)}
        >
          <p>{selectDoctorProtocolMessage}</p>
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminAssignCases;